/**
 * Body-appended ECharts HTML tooltips mis-position after nested scroll (mobile).
 * Track the real pointer in viewport coords and place tips with position:fixed.
 *
 * @see https://github.com/apache/echarts/issues/20876
 */

import { areEchartsTipsSuppressedForDrag } from '@/utils/echartsTooltipHide.js'

const POINTER_OFFSET_PX = 12
const MOVE_TO_PATCH_FLAG = '__ssPointerFixedMoveTo'

let lastPointerClient = null
let pointerListenersAttached = false
let postInitInstalled = false

/**
 * @param {PointerEvent | MouseEvent | Touch} event
 */
function recordPointerFromEvent(event) {
  if (!Number.isFinite(event?.clientX) || !Number.isFinite(event?.clientY)) {
    return
  }

  lastPointerClient = { x: event.clientX, y: event.clientY }
}

/**
 * @param {TouchEvent} event
 */
function onTouchEvent(event) {
  const touch = event.touches?.[0] || event.changedTouches?.[0]
  if (touch) recordPointerFromEvent(touch)
}

function ensurePointerListeners() {
  if (pointerListenersAttached || typeof document === 'undefined') return

  document.addEventListener('pointerdown', recordPointerFromEvent, true)
  document.addEventListener('pointermove', recordPointerFromEvent, true)
  // Older WebViews may not synthesize pointer events from touch.
  document.addEventListener('touchstart', onTouchEvent, true)
  document.addEventListener('touchmove', onTouchEvent, true)
  pointerListenersAttached = true
}

function removePointerListeners() {
  if (!pointerListenersAttached || typeof document === 'undefined') return

  document.removeEventListener('pointerdown', recordPointerFromEvent, true)
  document.removeEventListener('pointermove', recordPointerFromEvent, true)
  document.removeEventListener('touchstart', onTouchEvent, true)
  document.removeEventListener('touchmove', onTouchEvent, true)
  pointerListenersAttached = false
}

/**
 * @returns {{ x: number, y: number } | null}
 */
export function getLastPointerClient() {
  return lastPointerClient
}

/**
 * @param {number} x
 * @param {number} y
 * @param {HTMLElement} el
 * @returns {[number, number]}
 */
export function clampTooltipTopLeft(x, y, el) {
  const tipW = el.offsetWidth || 0
  const tipH = el.offsetHeight || 0
  const maxX = Math.max(0, window.innerWidth - tipW)
  const maxY = Math.max(0, window.innerHeight - tipH)

  return [
    Math.min(Math.max(0, x), maxX),
    Math.min(Math.max(0, y), maxY),
  ]
}

/**
 * @param {HTMLElement} el
 * @param {number} clientX
 * @param {number} clientY
 */
export function applyFixedTooltipAtClient(el, clientX, clientY) {
  if (!el?.style) return

  const [x, y] = clampTooltipTopLeft(
    clientX + POINTER_OFFSET_PX,
    clientY + POINTER_OFFSET_PX,
    el,
  )

  // Kill ECharts' default ~0.4s left/top/transform transition so the tip does
  // not paint/slide from the pre-patch coord into the pointer position.
  el.style.transition = 'none'
  el.style.position = 'fixed'
  el.style.left = `${Math.round(x)}px`
  el.style.top = `${Math.round(y)}px`
  el.style.right = 'auto'
  el.style.bottom = 'auto'
  el.style.transform = 'none'
}

/**
 * @param {Object} chart ECharts instance
 * @returns {Object | null} TooltipHTMLContent-like object
 */
function getTooltipHtmlContent(chart) {
  const views = chart?._componentsViews
  if (!Array.isArray(views)) return null

  const tooltipView = views.find((view) => view?.type === 'tooltip')
  const content = tooltipView?._tooltipContent
  if (!content || typeof content.moveTo !== 'function') return null
  // Rich-text tips have no HTML el / body container.
  if (!content.el) return null

  return content
}

/**
 * Replace moveTo so body-appended tips use viewport pointer coords.
 *
 * @param {Object} chart ECharts instance
 */
export function patchTooltipMoveToPointer(chart) {
  ensurePointerListeners()

  const content = getTooltipHtmlContent(chart)
  if (!content || content[MOVE_TO_PATCH_FLAG]) return

  content[MOVE_TO_PATCH_FLAG] = true
  const originalMoveTo = content.moveTo.bind(content)

  content.moveTo = function moveToNearPointer(zrX, zrY) {
    // Brush / drag-select: do not chase the pointer with a tip.
    if (areEchartsTipsSuppressedForDrag()) {
      const el = this.el
      if (el?.style) {
        el.style.display = 'none'
        el.style.visibility = 'hidden'
        el.style.opacity = '0'
      }
      return
    }

    const pointer = getLastPointerClient()
    const el = this.el
    const onBody = this._container === document.body

    if (pointer && el && onBody) {
      applyFixedTooltipAtClient(el, pointer.x, pointer.y)
      if (Array.isArray(this._styleCoord)) {
        this._styleCoord[0] = pointer.x
        this._styleCoord[1] = pointer.y
        const zrW = this._zr?.getWidth?.() || 1
        const zrH = this._zr?.getHeight?.() || 1
        this._styleCoord[2] = pointer.x / zrW
        this._styleCoord[3] = pointer.y / zrH
      }
      return
    }

    return originalMoveTo(zrX, zrY)
  }
}

/**
 * @param {Function} registerPostInit from echarts/core
 */
export function installEchartsTooltipPointerPosition(registerPostInit) {
  if (postInitInstalled) return

  if (typeof registerPostInit !== 'function') {
    throw new Error(
      'installEchartsTooltipPointerPosition requires registerPostInit',
    )
  }

  postInitInstalled = true
  ensurePointerListeners()
  registerPostInit((chart) => {
    const tryPatch = () => patchTooltipMoveToPointer(chart)
    tryPatch()
    // Tooltip view may appear on first setOption/render after init.
    chart?.on?.('rendered', tryPatch)
    chart?.on?.('showTip', tryPatch)
  })
}

/** @internal */
export const testables = {
  getPointerListenersAttached: () => pointerListenersAttached,
  isPostInitInstalled: () => postInitInstalled,
  reset() {
    lastPointerClient = null
    removePointerListeners()
    postInitInstalled = false
  },
  recordPointerForTests(x, y) {
    lastPointerClient = { x, y }
  },
}
